import React from "react";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Rating,
} from "@mui/material";

export default function ServiceCardDigital({
  service,
  onClick,
  isSlider = false,
  maxWidth = "auto",
}) {
  const cardStyle = isSlider
    ? {
        // Enhanced Modern AudioBook Card Design for Slider
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        borderRadius: "24px",
        overflow: "hidden",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        maxWidth: maxWidth,
        width: "100%",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(145deg, rgba(30, 30, 30, 0.9), rgba(50, 50, 50, 0.7))"
            : "linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(245, 245, 245, 0.8))",
        backdropFilter: "blur(20px)",
        border: (theme) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.05)",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 8px 32px rgba(0, 0, 0, 0.3)"
            : "0 8px 32px rgba(0, 0, 0, 0.1)",
        "&:hover": {
          transform: "translateY(-12px) scale(1.02)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 20px 60px rgba(0, 0, 0, 0.5)"
              : "0 20px 60px rgba(0, 0, 0, 0.15)",
        },
        "&:before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          opacity: 0,
          transition: "opacity 0.4s ease",
          zIndex: 1,
          pointerEvents: "none",
        },
        "&:hover:before": {
          opacity: 1,
        },
      }
    : {
        // Enhanced AudioBook Card Design for Grid
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        borderRadius: "16px",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        maxWidth: maxWidth === "auto" ? "260px" : maxWidth,
        width: "100%",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 12px 40px rgba(0,0,0,0.6)"
              : "0 12px 40px rgba(0,0,0,0.15)",
        },
        "&:before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
              : "linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.05) 100%)",
          opacity: 0,
          transition: "opacity 0.3s ease",
          zIndex: 1,
          pointerEvents: "none",
        },
        "&:hover:before": {
          opacity: 1,
        },
      };

  const imageBoxStyle = isSlider
    ? {
        position: "relative",
        overflow: "hidden",
        height: "180px",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.4) 100%)",
          opacity: 0,
          transition: "opacity 0.4s ease",
          zIndex: 2,
        },
        "&:hover::after": {
          opacity: 1,
        },
      }
    : {
        position: "relative",
        overflow: "hidden",
        height: "200px",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 60%, rgba(0,0,0,0.3) 100%)",
          opacity: 0,
          transition: "opacity 0.3s ease",
          zIndex: 2,
        },
        "&:hover::after": {
          opacity: 1,
        },
      };

  const imageStyle = isSlider
    ? {
        transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        filter: "brightness(1.05) contrast(1.1)",
        "&:hover": {
          transform: "scale(1.08)",
        },
      }
    : {
        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "scale(1.05)",
        },
      };

  const contentStyle = isSlider
    ? {
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        p: 2,
        position: "relative",
        zIndex: 2,
        background: "transparent",
      }
    : {
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        p: 2,
        position: "relative",
        zIndex: 2,
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(30, 30, 30, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
      };

  const titleStyle = isSlider
    ? {
        fontWeight: 700,
        mb: 0.5,
        lineHeight: 1.2,
        color: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.95)"
            : "rgba(0, 0, 0, 0.9)",
        transition: "color 0.3s ease",
        fontSize: "1rem",
        letterSpacing: "-0.02em",
      }
    : {
        fontWeight: 600,
        mb: 1,
        lineHeight: 1.3,
        color: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.95)"
            : "rgba(0, 0, 0, 0.87)",
        transition: "color 0.2s ease",
        fontSize: "1rem",
      };

  const subtitleStyle = isSlider
    ? {
        color: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.6)"
            : "rgba(0, 0, 0, 0.6)",
        lineHeight: 1.3,
        fontWeight: 500,
        fontSize: "0.8rem",
        letterSpacing: "0.01em",
        transition: "color 0.3s ease",
      }
    : {
        color: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.6)"
            : "rgba(0, 0, 0, 0.6)",
        lineHeight: 1.3,
        fontWeight: 500,
        fontSize: "0.8rem",
        letterSpacing: "0.01em",
        transition: "color 0.3s ease",
      };
  return (
    <Card onClick={() => onClick(service)} sx={cardStyle}>
      <Box sx={imageBoxStyle}>
        <CardMedia
          component="img"
          height={isSlider ? "180" : "200"}
          image={
            service.thumbnailImage
              ? `${process.env.BASEURL}${service.thumbnailImage}`
              : `https://via.placeholder.com/${isSlider ? "280" : "260"}x${
                  isSlider ? "180" : "200"
                }?text=No+Image`
          }
          alt={service.Service_Full_Name}
          sx={imageStyle}
        />
        {/* Rating positioned over the image at the bottom */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            background: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(10px)",
            borderRadius: "16px",
            px: 1,
            py: 0.25,
            zIndex: 3,
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
          }}
        >
          <Rating
            value={4.5}
            precision={0.5}
            size="small"
            readOnly
            sx={{
              "& .MuiRating-iconFilled": {
                color: "#FFD700",
                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
              },
              "& .MuiRating-iconEmpty": {
                color: "rgba(255, 255, 255, 0.3)",
              },
              "& .MuiRating-icon": {
                fontSize: "0.8rem",
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: "0.7rem",
              color: "rgba(255, 255, 255, 0.95)",
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.5)",
            }}
          >
            4.5
          </Typography>
        </Box>
      </Box>
      <CardContent sx={contentStyle}>
        <Typography variant="h6" component="div" sx={titleStyle}>
          {service.Service_Full_Name}
        </Typography>
        <Typography variant="body2" sx={subtitleStyle}>
          {service.Service_Short_Name}
        </Typography>
      </CardContent>
    </Card>
  );
}
